import { z } from 'zod';

export const nexusRegistrationSchema = z.object({
  id: z.string(),
  status: z.string().optional(),
});

export const nexusPeriodSchema = z.object({
  start_date: z.string(),
  end_date: z.string(),
  transactions_count: z.number(),
  transactions_included: z.number(),
  transactions_exempted: z.number(),
  transactions_marketplace: z.number(),
  transactions_amount: z.string(),
  transactions_tax_liability_amount: z.string(),
  nexus_state: z.string(),
  treatment_of_exempt_transactions: z.string(),
  sales_or_transactions: z.string(),
  threshold_sales: z.number().nullable(),
  threshold_transactions: z.number().nullable(),
  period_model: z.string(),
  include_marketplace_transactions: z.string(),
});

export const nexusSchema = z.object({
  id: z.string(),
  country_code: z.string(),
  state_code: z.string(),
  state_name: z.string().nullish(),
  status: z.string(),
  nexus_met_date: z.string().nullish(),
  earliest_collected_date: z.string().nullish(),
  earliest_transaction_date: z.string().nullish(),
  tax_liability: z
    .union([z.number(), z.string()])
    .transform(val => (typeof val === 'string' ? Number.parseFloat(val) : val)),
  vda_eligible: z.boolean().nullish(),
  organization_id: z.string(),
  registration: nexusRegistrationSchema.nullish(),
  registration_id: z.string().nullish(),
  collected_tax_nexus_met: z.boolean().nullish(),
  collected_tax_nexus_met_date: z.string().nullish(),
  economic_nexus_met: z.boolean().nullish(),
  economic_nexus_met_date: z.string().nullish(),
  physical_nexus_met: z.boolean().nullish(),
  physical_nexus_met_date: z.string().nullish(),
  most_recent_transaction_date: z.string().nullish(),
  nexus_met: z.boolean().nullish(),
  period_end_date: z.string().nullish(),
  period_model: z.string().nullish(),
  period_start_date: z.string().nullish(),
  periods: z.array(nexusPeriodSchema).nullish(),
  previous_period_end_date: z.string().nullish(),
  previous_period_start_date: z.string().nullish(),
  previous_transaction_count: z.number().nullish(),
  previous_transactions_amount: z
    .union([z.string(), z.number()])
    .transform(val => (typeof val === 'number' ? val.toString() : val))
    .nullish(),
  processing_status: z.string().nullish(),
  sales_or_transactions: z.string().nullish(),
  start_date: z.string().nullish(),
  threshold_sales: z.number().nullish(),
  threshold_transactions: z.number().nullish(),
  transaction_count: z.number().nullish(),
  transactions_amount: z
    .union([z.string(), z.number()])
    .transform(val => (typeof val === 'number' ? val.toString() : val))
    .nullish(),
  treatment_of_exempt_transactions: z.string().nullish(),
  trigger: z.string().nullish(),
  use_datetime: z.boolean().nullish(),
  currency: z.string().nullish(),
});

export const nexusUpdateSchema = z.object({
  processing_status: z.string().optional(),
  earliest_collected_date: z.string().optional(),
  vda_eligible: z.boolean().optional(),
  use_datetime: z.boolean().optional(),
});

export type Nexus = z.infer<typeof nexusSchema>;
export type NexusPeriod = z.infer<typeof nexusPeriodSchema>;
export type NexusRegistration = z.infer<typeof nexusRegistrationSchema>;
export type NexusUpdate = z.infer<typeof nexusUpdateSchema>;

export const nexusPhysicalSchema = z.object({
  id: z.string(),
  country_code: z.string(),
  state_code: z.string(),
  start_date: z.string(),
  end_date: z.string().nullable(),
  category: z.string(),
  external_id: z.string().nullable(),
  source: z.string(),
  street_1: z.string().nullish(),
  street_2: z.string().nullish(),
  city: z.string().nullish(),
  postal_code: z.string().nullish(),
});

export type NexusPhysical = z.infer<typeof nexusPhysicalSchema>;
